import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import Select, { components, createFilter } from 'react-select';
import { useStoreState } from 'easy-peasy';

import { Typography } from "stories/components"
import "stories/forms/SelectMulti.css"
import "./StockMultiSelect.css"

// Arvopaperi filter for transaction lists (#2344). Unlike StockSelect it never shows chips:
// one choice shows its name, several show "N arvopaperia", so the filter row keeps its height.
// An empty selection means all securities.
//
// "Valitse näkyvät" and "Tyhjennä" are the first two options of the menu, so they work with the
// arrow keys and Enter and react-select announces them like any other option. They never end up
// in the value.

export const SELECT_VISIBLE = "__select_visible__"
export const CLEAR_ALL = "__clear_all__"

const isAction = (option) => option?.value === SELECT_VISIBLE || option?.value === CLEAR_ALL

// react-select's default filter, also used to work out which securities are "shown".
const securityFilter = createFilter()

const matchesSearch = (option, inputValue) =>
  securityFilter({ label: option.label, value: String(option.value), data: option }, inputValue)

const filterOption = (candidate, inputValue) => isAction(candidate.data) || securityFilter(candidate, inputValue)

const toOption = (stock) => ({
  value: stock?.id,
  label: stock?.name,
  related_model_type: stock?.related_model_type,
  related_model_id: stock?.related_model_id,
})

const toValue = (option) => ({
  id: option?.value,
  name: option?.label,
  related_model_type: option?.related_model_type,
  related_model_id: option?.related_model_id,
})

const ValueContainer = ({ children, ...props }) => {
  const { getValue, selectProps } = props
  const selected = getValue()
  // react-select passes [placeholder-or-values, input]
  const [, input] = children

  if (selected.length === 0) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>
  }

  // While the user types, the search text replaces the summary.
  const summary = selected.length === 1 ? selected[0].label : selectProps.summaryText(selected.length)
  return (
    <components.ValueContainer {...props}>
      {!selectProps.inputValue && <div className="stock-multi-select__summary" title={summary}>{summary}</div>}
      {input}
    </components.ValueContainer>
  )
}

const Option = (props) => {
  if (isAction(props.data)) {
    const className = props.data.value === CLEAR_ALL
      ? "stock-multi-select__option--action stock-multi-select__option--clear"
      : "stock-multi-select__option--action"
    return (
      <components.Option {...props} className={className}>
        <span>{props.label}</span>
      </components.Option>
    )
  }
  return (
    <components.Option {...props}>
      <input type="checkbox" className="stock-multi-select__checkbox" checked={props.isSelected} readOnly tabIndex={-1} />
      <span>{props.label}</span>
    </components.Option>
  )
}

// `options` (optional, [{id, name, related_model_type, related_model_id}]) replaces the stocks
// store as the source of choices, e.g. the securities found in a report (#2429). Without it the
// component lists the store's stocks as before.
const StockMultiSelect = ({ label, name = "selected_securities", value, setFieldValue, options: ownOptions, isLoading, ...rest }) => {
  const { t } = useTranslation();

  const stocks = useStoreState((state) => state.stocks?.objects);
  const storeLoading = useStoreState((state) => state.stocks?.loading);

  const source = ownOptions !== undefined ? ownOptions : stocks
  const loading = ownOptions !== undefined ? Boolean(isLoading) : storeLoading

  const [inputValue, setInputValue] = useState("")
  const [liveMessage, setLiveMessage] = useState("")

  const securityOptions = useMemo(() => (source || []).map(toOption), [source])

  const selectedOptions = useMemo(() => {
    const ids = new Set((value || []).map(obj => obj?.id))
    return securityOptions.filter(option => ids.has(option.value))
  }, [value, securityOptions])

  // The securities the menu shows for the current search text.
  const visible = useMemo(
    () => securityOptions.filter(option => matchesSearch(option, inputValue)),
    [securityOptions, inputValue]
  )

  const selectVisibleText = t("security_multi_select_select_visible", "Valitse näkyvät")
  const clearText = t("security_multi_select_clear", "Tyhjennä")

  // Stable objects, so react-select keeps the focus on an action row after it is chosen.
  const actionOptions = useMemo(() => [
    { value: SELECT_VISIBLE, label: selectVisibleText },
    { value: CLEAR_ALL, label: clearText },
  ], [selectVisibleText, clearText])

  // The actions are shown only when the search matches at least one security.
  const options = useMemo(
    () => (visible.length > 0 ? [...actionOptions, ...securityOptions] : securityOptions),
    [visible.length, actionOptions, securityOptions]
  )

  // onChange reads the latest render's values, not a stale closure.
  const latest = useRef({})
  latest.current = { visible, selectedOptions }

  const setSelected = useCallback((selected) => {
    setFieldValue(name, (selected || []).map(toValue))
  }, [name, setFieldValue])

  const handleChange = useCallback((newValue, meta) => {
    const option = meta?.option
    if (isAction(option)) {
      if (option.value === CLEAR_ALL) {
        setSelected([])
        setLiveMessage(t("security_multi_select_live_cleared", "Valinta tyhjennetty: kaikki arvopaperit"))
        return
      }
      const { visible: shown, selectedOptions: current } = latest.current
      const ids = new Set(current.map(o => o.value))
      const union = [...current, ...shown.filter(o => !ids.has(o.value))]
      setSelected(union)
      setLiveMessage(t("security_multi_select_live_selected", "Valittuja arvopapereita: {{count}}", { count: union.length }))
      return
    }
    setSelected((newValue || []).filter(o => !isAction(o)))
    // Picking a single security clears the search, as before.
    if (meta?.action === "select-option" || meta?.action === "deselect-option") {
      setInputValue("")
    }
  }, [setSelected, t])

  const handleInputChange = useCallback((newInput, { action }) => {
    // react-select clears the input before every selection; the action rows keep the search.
    if (action === "set-value") return
    setInputValue(newInput)
  }, [])

  const accessibleName = label || t("security_select_label", "Arvopaperi")

  return (
    <div className="stock-multi-select w-100">
      <Typography bold={false} className="***">{accessibleName}</Typography>
      <Select
        className="select_multi_select_large"
        classNamePrefix="stock-multi-select"
        name={name}
        aria-label={accessibleName}
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        tabSelectsValue={false}
        isLoading={loading}
        options={options}
        filterOption={filterOption}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={t("Kaikki")}
        noOptionsMessage={() => t("No choices")}
        components={{ ValueContainer, Option }}
        summaryText={(count) => t("security_multi_select_count", "{{count}} arvopaperia", { count })}
        styles={{ container: (provided) => ({ ...provided, width: '100%' }) }}
        {...rest}
      />
      <span className="sr-only" role="status" aria-live="polite">{liveMessage}</span>
    </div>
  )
}

export default StockMultiSelect;
